import { GRAPH } from "./queryable-graph"
import {
    TypedQueryPayload,
    ResourceName,
    ValidPath,
    AggregateField,
    TimeBucket,
} from "./queryableTypes"

// Define a strict record of caster functions using `unknown` instead of `any`
const CASTERS: Record<string, (v: unknown) => unknown> = {
    number: (v: unknown) => (v === null ? null : Number(v)),
    boolean: (v: unknown) => (v === null ? null : Boolean(v)),
    // Use type assertion on `v` to satisfy Date constructor, but keep input strictly unknown
    date: (v: unknown) => (v === null ? null : new Date(v as string | number | Date)),
    json: (v: unknown) => v,
    string: (v: unknown) => (v === null ? null : String(v)),
}

export function castQueryResults<
    R extends ResourceName,
    S extends ReadonlyArray<ValidPath<R>> | undefined,
    A extends ReadonlyArray<AggregateField<R>> | undefined,
    T extends ReadonlyArray<TimeBucket<R>> | undefined,
>(
    // Use Record<string, unknown> for database rows instead of any[]
    rows: Record<string, unknown>[],
    payload: TypedQueryPayload<R, S, A, T>
): Record<string, unknown>[] {
    if (!rows || rows.length === 0) return rows

    // 1. Build a "Type Map" for this specific query
    const typeMap = new Map<string, (v: unknown) => unknown>()

    // A. Handle 'select' columns
    if (payload.select) {
        payload.select.forEach((path) => {
            const pathStr = path as string
            const typeStr = getTypeForPath(payload.resource, pathStr)
            // Strip nullable suffix (e.g. "number?" → "number") before looking up caster
            const baseType = typeStr ? typeStr.replace("?", "") : null
            if (baseType && Object.hasOwn(CASTERS, baseType)) {
                typeMap.set(pathStr, CASTERS[baseType])
            }
        })
    }

    // B. Handle 'timeBuckets'
    if (payload.timeBuckets) {
        payload.timeBuckets.forEach((tb) => {
            typeMap.set(tb.alias, CASTERS.date)
        })
    }

    // C. Handle 'aggregates'
    if (payload.aggregates) {
        payload.aggregates.forEach((agg) => {
            if (["COUNT", "SUM", "AVG"].includes(agg.func)) {
                typeMap.set(agg.alias, CASTERS.number)
            } else if (agg.func === "JSON_AGG") {
                typeMap.set(agg.alias, CASTERS.json)
            } else {
                const fieldType = getTypeForPath(payload.resource, agg.field as string)
                const baseFieldType = fieldType ? fieldType.replace("?", "") : null
                if (baseFieldType && baseFieldType in CASTERS) {
                    typeMap.set(agg.alias, CASTERS[baseFieldType])
                }
            }
        })
    }

    // 2. Apply the map to every row
    return rows.map((row) => {
        const newRow: Record<string, unknown> = { ...row }
        typeMap.forEach((caster, key) => {
            if (key in newRow) {
                newRow[key] = caster(newRow[key])
            }
        })
        return newRow
    })
}

// Extract a union type of all possible Graph nodes
type GraphNode = (typeof GRAPH)[ResourceName]

// Type-safe graph traversal without `any`
function getTypeForPath(resource: ResourceName, path: string): string | null {
    const parts = path.split(".")
    let currentNode: GraphNode = GRAPH[resource]

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i]
        const isLastPart = i === parts.length - 1

        if (isLastPart) {
            // Safely check schema
            if ("schema" in currentNode && Object.hasOwn(currentNode.schema, part)) {
                return currentNode.schema[part as keyof typeof currentNode.schema] as string
            }
            // Safely check computed
            if ("computed" in currentNode && Object.hasOwn(currentNode.computed, part)) {
                const entry = currentNode.computed[part as keyof typeof currentNode.computed] as
                    | string
                    | { expr: string; type: string }
                return typeof entry === "string" ? "number" : entry.type
            }
        }

        // Safely traverse edges
        if ("edges" in currentNode && Object.hasOwn(currentNode.edges, part)) {
            const edge = currentNode.edges[part as keyof typeof currentNode.edges] as {
                resource: ResourceName
                on: string
            }
            currentNode = GRAPH[edge.resource]
            continue
        }
    }
    return null
}
